import { useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  adminApproveVersion,
  adminCreateDraft,
  adminFeatureToday,
  adminGetDrinkHistory,
  adminGetVersions,
  adminRestoreAsDraft,
  adminRetireVersion,
  adminSetServiceStatus,
  getDrink,
  type AuditLogEntry,
  type DrinkServiceStatus,
} from "../../lib/api";
import ApproveModal from "../../components/admin/ApproveModal";
import ChangeHistoryList from "../../components/admin/ChangeHistoryList";
import type {
  AdminVersionRow,
  AdminVersionsResponse,
} from "../../types/admin";
import type { DrinkDetail } from "../../types/drink";
import type { SessionUser } from "../../types/auth";
import { useConfirmDialog } from "../../components/ConfirmDialog";

const DESKTOP_BREAKPOINT = 1024;

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.innerWidth >= DESKTOP_BREAKPOINT
  );
  useEffect(() => {
    const onResize = () => setDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return desktop;
}

export default function AdminRecipeDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useOutletContext<{ user: SessionUser }>();
  const isOwner = user.bar_roles.includes("bar_owner");
  const isManager = isOwner || user.bar_roles.includes("bar_manager");

  const [detail, setDetail] = useState<DrinkDetail | null>(null);
  const [versions, setVersions] = useState<AdminVersionsResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [history, setHistory] = useState<AuditLogEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [approveTarget, setApproveTarget] = useState<{
    versionId: string;
    versionNumber: string;
    drinkName: string;
    hasPreviousApproved: boolean;
  } | null>(null);

  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyServiceCtl, setBusyServiceCtl] = useState<boolean>(false);
  const [serviceCtlError, setServiceCtlError] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  // Distinguishes "fetch failed" (loadError set, detail null) from
  // "fetch succeeded but no approved version yet" (loadError null,
  // detail null) so we don't show a misleading empty state when the
  // network is the actual problem.
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoadingDetail(true);
    setLoadingHistory(true);
    setDetail(null);
    setVersions(null);
    setHistory([]);
    setActionError(null);
    setLoadError(null);
    Promise.all([
      getDrink(slug).then((d) => ({ ok: true as const, d })).catch((e) => ({ ok: false as const, e })),
      adminGetVersions(slug).then((v) => ({ ok: true as const, v })).catch((e) => ({ ok: false as const, e })),
    ]).then(([dr, vr]) => {
      if (dr.ok) setDetail(dr.d);
      if (vr.ok) setVersions(vr.v);
      if (!dr.ok || !vr.ok) {
        const err = (dr.ok ? null : dr.e) || (vr.ok ? null : vr.e);
        setLoadError(err?.message ?? "Could not load recipe");
      }
      setLoadingDetail(false);
    });
    void adminGetDrinkHistory(slug)
      .then((h) => setHistory(h.history))
      .catch(() => undefined)
      .finally(() => setLoadingHistory(false));
  }, [slug]);

  function reloadAll() {
    if (!slug) return;
    setLoadingDetail(true);
    setLoadingHistory(true);
    setLoadError(null);
    Promise.all([
      getDrink(slug).then((d) => ({ ok: true as const, d })).catch((e) => ({ ok: false as const, e })),
      adminGetVersions(slug).then((v) => ({ ok: true as const, v })).catch((e) => ({ ok: false as const, e })),
    ]).then(([dr, vr]) => {
      if (dr.ok) setDetail(dr.d);
      if (vr.ok) setVersions(vr.v);
      if (!dr.ok || !vr.ok) {
        const err = (dr.ok ? null : dr.e) || (vr.ok ? null : vr.e);
        setLoadError(err?.message ?? "Could not reload");
      }
      setLoadingDetail(false);
    });
    void adminGetDrinkHistory(slug)
      .then((h) => setHistory(h.history))
      .catch(() => undefined)
      .finally(() => setLoadingHistory(false));
  }

  async function onCreateDraftFromApproved() {
    if (!slug) return;
    if (!await confirm({
      title: "Clone Approved version into a new Draft?",
      message: "You can edit the Draft without affecting the live recipe.",
      confirmLabel: "Create Draft",
    })) return;
    try {
      const resp = await adminCreateDraft(slug);
      navigate(`/admin/recipes/${slug}/edit/${resp.version_id}`);
    } catch (err: any) {
      setActionError(err?.message ?? "Could not create draft");
    }
  }

  function onEditVersion(versionId: string) {
    if (!slug) return;
    navigate(`/admin/recipes/${slug}/edit/${versionId}`);
  }

  function onOpenApprove(v: AdminVersionRow) {
    setActionError(null);
    setApproveTarget({
      versionId: v.id,
      versionNumber: v.version_number,
      drinkName: detail?.drink.name ?? slug ?? "(unknown)",
      hasPreviousApproved: !!detail?.drink && !!detail.version,
    });
  }

  async function onConfirmApprove(changeSummary: string) {
    if (!approveTarget) return;
    try {
      await adminApproveVersion(approveTarget.versionId, changeSummary);
      setApproveTarget(null);
      reloadAll();
    } catch (err: any) {
      throw err;
    }
  }

  async function onRetireVersion(v: AdminVersionRow) {
    if (!await confirm({
      title: `Retire v${v.version_number}?`,
      message: v.status === "Approved"
        ? "Bartenders will see no live recipe for this drink until you approve another version."
        : undefined,
      confirmLabel: "Retire",
      danger: true,
    })) return;
    setActionError(null);
    setBusyVersionId(v.id);
    try {
      await adminRetireVersion(v.id);
      reloadAll();
    } catch (err: any) {
      setActionError(err?.message ?? "Could not retire");
    } finally {
      setBusyVersionId(null);
    }
  }

  async function onSetServiceStatus(newStatus: DrinkServiceStatus) {
    if (!detail) return;
    const previous = detail;
    setServiceCtlError(null);
    setBusyServiceCtl(true);
    // Optimistic — service-status toggles felt slow because the round-trip was
    // followed by 3 more fetches (reloadAll). Now flip locally first; on error
    // roll back and re-sync with a fresh reload.
    setDetail({ ...detail, drink: { ...detail.drink, service_status: newStatus } });
    try {
      await adminSetServiceStatus(detail.drink.id, newStatus);
    } catch (err: any) {
      setDetail(previous);
      setServiceCtlError(err?.message ?? "Could not update service status");
    } finally {
      setBusyServiceCtl(false);
    }
  }

  async function onToggleFeatureToday() {
    if (!detail) return;
    const previous = detail;
    const wasFeatured = !!detail.drink.is_featured_today;
    setServiceCtlError(null);
    setBusyServiceCtl(true);
    setDetail({ ...detail, drink: { ...detail.drink, is_featured_today: !wasFeatured } });
    try {
      await adminFeatureToday(detail.drink.id, !wasFeatured);
    } catch (err: any) {
      setDetail(previous);
      setServiceCtlError(err?.message ?? "Could not toggle Featured Today");
    } finally {
      setBusyServiceCtl(false);
    }
  }

  async function onRestoreAsDraft(v: AdminVersionRow) {
    if (!await confirm({
      title: `Restore v${v.version_number} as a new Draft?`,
      message: "The original Retired version stays untouched; you'll edit a fresh copy.",
      confirmLabel: "Restore",
    })) return;
    setActionError(null);
    setBusyVersionId(v.id);
    try {
      const resp = await adminRestoreAsDraft(v.id);
      navigate(`/admin/recipes/${slug}/edit/${resp.version_id}`);
    } catch (err: any) {
      setActionError(err?.message ?? "Could not restore");
    } finally {
      setBusyVersionId(null);
    }
  }

  return (
    <div>
      <Link to="/admin/recipes" style={styles.backLink}>
        ← Back to recipes
      </Link>
      <div style={isDesktop ? styles.twoColShell : styles.stackShell}>
        <RecipeView
          detail={detail}
          loading={loadingDetail}
          loadError={loadError}
          onRetry={reloadAll}
          slug={slug ?? null}
          isManager={isManager}
          busyServiceCtl={busyServiceCtl}
          serviceCtlError={serviceCtlError}
          onSetServiceStatus={onSetServiceStatus}
          onToggleFeatureToday={onToggleFeatureToday}
          onCreateDraftFromApproved={onCreateDraftFromApproved}
          onEditAliases={() => slug && navigate(`/admin/recipes/${slug}/aliases`)}
        />
        <VersionsView
          versions={versions}
          loading={loadingDetail}
          drinkLoaded={!!detail}
          onEditVersion={onEditVersion}
          onApproveVersion={onOpenApprove}
          onRetireVersion={onRetireVersion}
          onRestoreAsDraft={onRestoreAsDraft}
          busyVersionId={busyVersionId}
          actionError={actionError}
          isManager={isManager}
          isOwner={isOwner}
          history={history}
          loadingHistory={loadingHistory}
        />
      </div>
      {approveTarget && (
        <ApproveModal
          open={!!approveTarget}
          versionNumber={approveTarget.versionNumber}
          drinkName={approveTarget.drinkName}
          hasPreviousApproved={approveTarget.hasPreviousApproved}
          onClose={() => setApproveTarget(null)}
          onApprove={onConfirmApprove}
        />
      )}
      {confirmDialog}
    </div>
  );
}

// ─────────────────────────── Recipe View ───────────────────────────

interface RecipeViewProps {
  detail: DrinkDetail | null;
  loading: boolean;
  loadError: string | null;
  onRetry: () => void;
  slug: string | null;
  isManager: boolean;
  busyServiceCtl: boolean;
  serviceCtlError: string | null;
  onSetServiceStatus: (status: DrinkServiceStatus) => void;
  onToggleFeatureToday: () => void;
  onCreateDraftFromApproved: () => void;
  onEditAliases: () => void;
}

function RecipeView(p: RecipeViewProps) {
  if (p.loading) {
    return (
      <section style={styles.viewPanel}>
        <p style={styles.muted}>Loading…</p>
      </section>
    );
  }
  if (p.loadError) {
    return (
      <section style={styles.viewPanel}>
        <p style={{ ...styles.muted, color: "#FF8A7A" }}>{p.loadError}</p>
        <button
          type="button"
          onClick={p.onRetry}
          style={{
            background: "transparent", border: "1px solid var(--border)",
            color: "var(--text)", padding: "8px 16px", borderRadius: 8,
            minHeight: 40, fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}
        >
          Retry
        </button>
      </section>
    );
  }
  if (!p.detail) {
    return (
      <section style={styles.viewPanel}>
        <p style={styles.muted}>Recipe has no approved version yet.</p>
      </section>
    );
  }

  const { drink, version, ingredients, finish_steps } = p.detail;
  const hasApproved = !!version;
  return (
    <section style={styles.viewPanel}>
      <header style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
          {version && (
            <Chip
              label={`✓ ${version.status}`}
              tone={version.status === "Approved" ? "approved" : "muted"}
            />
          )}
          {version && <Chip label={`v${version.version_number}`} tone="muted" />}
          <Chip label={drink.service_status} tone="muted" />
          {drink.is_featured_today && <Chip label="Featured Today" tone="gold" />}
        </div>
        <h2 style={styles.recipeName}>{drink.name}</h2>
        <p style={styles.tags}>
          {drink.category} · {drink.tags.join(" · ")}
        </p>
      </header>

      {version?.warning && (
        <div style={styles.warning}>
          <span style={{ marginRight: 6 }}>⚠</span>
          {version.warning}
        </div>
      )}

      {version && (
        <div style={styles.metaGrid}>
          <Meta label="Glass" value={version.glass} />
          <Meta label="Ice" value={version.ice} />
          <Meta label="Method" value={version.method} />
          <Meta label="Garnish" value={version.garnish} />
          <Meta label="Vessel" value={version.vessel} />
          <Meta label="Smoked" value={version.smoked ? "Yes" : "No"} />
          <Meta label="Frozen" value={version.frozen ? "Yes" : "No"} />
        </div>
      )}

      <Section title="Ingredients">
        <ul style={styles.ingList}>
          {ingredients.map((ing) => {
            const isUntracked = !!ing.ingredient.is_untracked;
            const unavail = !isUntracked && ing.ingredient.available === false;
            return (
              <li key={ing.id} style={{ ...styles.ingRow, ...(unavail ? styles.ingRowUnavail : {}) }}>
                <span style={styles.ingAmount}>
                  {ing.amount} {ing.unit}
                </span>
                <span style={styles.ingName}>
                  {ing.display_name}
                  {isUntracked && <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, marginLeft: 6 }}>· basic staple</span>}
                  {unavail && <span style={styles.unavailFlag}> · unavailable</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section title="Build">
        {version?.instructions && version.instructions.length > 0 ? (
          <ol style={styles.steps}>
            {version.instructions.map((step, i) => (
              <li key={i} style={styles.step}>
                <span style={styles.stepNum}>{i + 1}</span>
                <span style={styles.stepText}>{step}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p style={styles.subsectionEmpty}>
            No build steps recorded for this version yet.
          </p>
        )}

        <h4 style={styles.subsectionTitle}>Finish</h4>
        {finish_steps && finish_steps.length > 0 ? (
          <ul style={styles.finishList}>
            {finish_steps.map((fs) => (
              <li key={fs.id} style={styles.finishRow}>
                <div style={styles.finishHeader}>
                  <span style={styles.finishTypeChip}>{fs.finish_type}</span>
                  <strong style={styles.finishLabel}>{fs.label}</strong>
                  {!fs.required && <span style={styles.finishOptional}>optional</span>}
                  {fs.scale_type === "batch_finish" && (
                    <span style={styles.finishBatch}>batch · once per round</span>
                  )}
                  {fs.scale_type === "do_not_scale" && (
                    <span style={styles.finishBatch}>×1 (does not scale)</span>
                  )}
                </div>
                {fs.instruction && (
                  <p style={styles.finishInstruction}>{fs.instruction}</p>
                )}
                {(fs.quantity != null || fs.unit) && (
                  <p style={styles.finishQty}>
                    {fs.quantity != null ? fs.quantity : ""}
                    {fs.unit ? ` ${fs.unit}` : ""}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p style={styles.subsectionEmpty}>
            No separate finish steps recorded — finishing / garnishing /
            smoking instructions may be in the build steps above. Add
            structured finish steps in the recipe editor (Section F).
          </p>
        )}
      </Section>

      {version?.staff_notes && (
        <Section title="Staff Note">
          <p style={styles.notes}>{version.staff_notes}</p>
        </Section>
      )}

      {p.isManager && (
        <div
          style={styles.serviceCtlBlock}
          role="group"
          aria-labelledby="service-ctl-heading"
        >
          <div style={styles.serviceCtlHeader}>
            <span id="service-ctl-heading" style={styles.eyebrow}>Service controls</span>
            {p.busyServiceCtl && <span style={styles.serviceCtlBusy}>Updating…</span>}
          </div>
          {p.serviceCtlError && (
            <div style={styles.serviceCtlError} role="alert">{p.serviceCtlError}</div>
          )}
          <div style={styles.serviceCtlRow}>
            <ServiceCtlButton
              label={drink.service_status === "Active" ? "Active ✓" : "Set Active"}
              active={drink.service_status === "Active"}
              disabled={p.busyServiceCtl || !hasApproved}
              title={!hasApproved ? "No Approved version" : ""}
              onClick={() => p.onSetServiceStatus("Active")}
            />
            <ServiceCtlButton
              label={drink.service_status === "86d" ? "86'd ✓" : "86 it"}
              active={drink.service_status === "86d"}
              tone="red"
              disabled={p.busyServiceCtl || !hasApproved}
              onClick={() => p.onSetServiceStatus("86d")}
            />
            <ServiceCtlButton
              label={drink.service_status === "Hidden" ? "Hidden ✓" : "Hide"}
              active={drink.service_status === "Hidden"}
              tone="muted"
              disabled={p.busyServiceCtl}
              onClick={() => p.onSetServiceStatus("Hidden")}
            />
            <ServiceCtlButton
              label={drink.is_featured_today ? "Unfeature Today" : "Feature Today"}
              active={!!drink.is_featured_today}
              tone="gold"
              disabled={p.busyServiceCtl || !hasApproved}
              onClick={p.onToggleFeatureToday}
            />
          </div>
        </div>
      )}

      <div style={styles.adminActionRow}>
        <button
          type="button"
          onClick={p.onCreateDraftFromApproved}
          style={styles.adminAction}
        >
          Create Draft From Approved
        </button>
        {p.isManager && (
          <a
            href={`/admin/recipes/${drink.slug}/aliases`}
            onClick={(e) => {
              e.preventDefault();
              p.onEditAliases();
            }}
            style={styles.aliasesLink}
            title="Owner only — edit bartender search shortcuts"
          >
            Edit search aliases
          </a>
        )}
        <span style={styles.phaseHint}>
          Use the Versions panel to edit existing drafts.
        </span>
      </div>
    </section>
  );
}

// ─────────────────────────── Service Ctl Button ───────────────────────────

interface ServiceCtlButtonProps {
  label: string;
  active: boolean;
  tone?: "gold" | "red" | "muted";
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}

function ServiceCtlButton({ label, active, tone, disabled, title, onClick }: ServiceCtlButtonProps) {
  const baseStyle: CSSProperties = {
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
  if (active) {
    if (tone === "red") {
      baseStyle.background = "rgba(224,80,64,0.18)";
      baseStyle.color = "#FF8A7A";
      baseStyle.borderColor = "rgba(224,80,64,0.5)";
    } else if (tone === "gold" || !tone) {
      baseStyle.background = "var(--gold)";
      baseStyle.color = "#0D0B08";
      baseStyle.borderColor = "var(--gold)";
    } else {
      baseStyle.background = "var(--surface)";
      baseStyle.color = "var(--muted)";
    }
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} style={baseStyle}>
      {label}
    </button>
  );
}

// ─────────────────────────── Versions View ───────────────────────────

interface VersionsViewProps {
  versions: AdminVersionsResponse | null;
  loading: boolean;
  drinkLoaded: boolean;
  onEditVersion: (versionId: string) => void;
  onApproveVersion: (v: AdminVersionRow) => void;
  onRetireVersion: (v: AdminVersionRow) => void;
  onRestoreAsDraft: (v: AdminVersionRow) => void;
  busyVersionId: string | null;
  actionError: string | null;
  isManager: boolean;
  isOwner: boolean;
  history: AuditLogEntry[];
  loadingHistory: boolean;
}

function VersionsView(p: VersionsViewProps) {
  if (!p.drinkLoaded && !p.loading) {
    return null;
  }
  if (p.loading) {
    return (
      <section style={styles.versionPanel}>
        <p style={styles.muted}>Loading versions…</p>
      </section>
    );
  }
  if (!p.versions || p.versions.versions.length === 0) {
    return (
      <section style={styles.versionPanel}>
        <p style={styles.muted}>No versions on file.</p>
      </section>
    );
  }

  return (
    <section style={styles.versionPanel}>
      <h3 style={styles.sectionTitle}>Versions</h3>
      {p.actionError && <div style={styles.versionError}>{p.actionError}</div>}
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
        {p.versions.versions.map((v) => {
          const isApproved = v.status === "Approved";
          const isRetired = v.status === "Retired" || v.status === "Archived";
          const isEditable = !isApproved && !isRetired;
          const isPending = v.status === "Draft" || v.status === "Testing" || v.status === "Needs Review";
          const busy = p.busyVersionId === v.id;
          return (
            <li key={v.id} style={styles.versionRow}>
              <div style={styles.versionRowTop}>
                <span style={styles.versionNumber}>v{v.version_number}</span>
                <Chip
                  label={v.status}
                  tone={isApproved ? "approved" : "muted"}
                />
              </div>
              <div style={styles.versionMeta}>
                {v.approved_at &&
                  `Approved ${new Date(v.approved_at).toLocaleDateString()}${v.approved_by_name ? ` by ${v.approved_by_name}` : ""}`}
                {!v.approved_at &&
                  `Created ${new Date(v.created_at).toLocaleDateString()}${v.created_by_name ? ` by ${v.created_by_name}` : ""}`}
              </div>
              {v.change_summary && (
                <p style={styles.versionChangeSummary}>{v.change_summary}</p>
              )}
              <div style={styles.versionActions}>
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => p.onEditVersion(v.id)}
                    disabled={busy}
                    style={styles.editVersionBtn}
                  >
                    Edit
                  </button>
                )}
                {isPending && p.isOwner && (
                  <button
                    type="button"
                    onClick={() => p.onApproveVersion(v)}
                    disabled={busy}
                    style={styles.approveBtn}
                    title="Approve & Publish (owner only)"
                  >
                    Approve & Publish
                  </button>
                )}
                {isPending && !p.isOwner && (
                  <span style={styles.subtleNote}>
                    Owner approval required.
                  </span>
                )}
                {(isApproved || isPending) && p.isManager && (
                  <button
                    type="button"
                    onClick={() => p.onRetireVersion(v)}
                    disabled={busy}
                    style={styles.retireBtn}
                  >
                    {isApproved ? "Retire" : "Retire draft"}
                  </button>
                )}
                {isRetired && (
                  <button
                    type="button"
                    onClick={() => p.onRestoreAsDraft(v)}
                    disabled={busy}
                    style={styles.restoreBtn}
                  >
                    Restore as new draft
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <h3 style={{ ...styles.sectionTitle, marginTop: 16 }}>Change history</h3>
      <ChangeHistoryList entries={p.history} loading={p.loadingHistory} />
    </section>
  );
}

// ─────────────────────────── Tiny helpers ───────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

function Meta({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={styles.metaBox}>
      <p style={styles.metaLabel}>{label}</p>
      <p style={styles.metaValue}>{value ?? "—"}</p>
    </div>
  );
}

function Chip({ label, tone }: { label: string; tone: "gold" | "muted" | "approved" }) {
  return <span style={{ ...styles.chip, ...chipTone[tone] }}>{label}</span>;
}

const chipTone: Record<string, CSSProperties> = {
  gold:     { background: "rgba(249,175,54,0.12)", color: "var(--gold)", borderColor: "rgba(249,175,54,0.3)" },
  muted:    { background: "var(--bg)",             color: "var(--muted)", borderColor: "var(--border)"      },
  approved: { background: "rgba(39,174,96,0.16)",  color: "#86efac",     borderColor: "rgba(39,174,96,0.3)" },
};

// ─────────────────────────── Styles ───────────────────────────

const styles: Record<string, CSSProperties> = {
  backLink: {
    display: "inline-block",
    color: "var(--muted)",
    fontWeight: 700,
    fontSize: 13,
    textDecoration: "none",
    marginBottom: 12,
  },
  twoColShell: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 340px)",
    gap: 12,
    alignItems: "flex-start",
  },
  stackShell: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
  },
  viewPanel: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 16,
    minWidth: 0,
  },
  recipeName: {
    fontFamily: "Georgia, serif",
    margin: "4px 0 4px",
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: -0.4,
  },
  tags: { margin: 0, fontSize: 12, color: "var(--muted)", fontWeight: 600 },
  warning: {
    padding: 10,
    borderRadius: 10,
    background: "rgba(249,175,54,0.08)",
    border: "1px solid rgba(249,175,54,0.3)",
    color: "var(--gold)",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
  },
  metaGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12 },
  metaBox: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
  },
  metaLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  metaValue: { margin: "4px 0 0", fontSize: 13, fontWeight: 800, lineHeight: 1.25 },
  section: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
  },
  sectionTitle: {
    margin: "0 0 8px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  ingList: { listStyle: "none", padding: 0, margin: 0 },
  ingRow: {
    display: "grid",
    gridTemplateColumns: "100px 1fr",
    gap: 10,
    padding: "8px 4px",
    borderBottom: "1px solid var(--border)",
  },
  ingRowUnavail: { color: "#FF8A7A" },
  ingAmount: { fontSize: 14, fontWeight: 800 },
  ingName: { fontSize: 14, fontWeight: 600 },
  unavailFlag: { fontSize: 11, fontWeight: 700, color: "#FF8A7A" },
  steps: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 },
  step: {
    display: "grid",
    gridTemplateColumns: "24px 1fr",
    gap: 8,
    padding: 8,
    background: "var(--surface)",
    borderRadius: 8,
  },
  stepNum: {
    width: 22,
    height: 22,
    borderRadius: 999,
    background: "var(--gold)",
    color: "#0D0B08",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 12,
  },
  stepText: { fontSize: 13, lineHeight: 1.3 },
  notes: { margin: 0, fontSize: 13, lineHeight: 1.4, color: "var(--text)" },
  subsectionTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 16,
    fontWeight: 700,
    margin: "16px 0 8px",
    paddingTop: 12,
    borderTop: "1px solid var(--border)",
    color: "var(--text)",
    letterSpacing: -0.2,
  },
  subsectionEmpty: {
    fontSize: 12,
    color: "var(--muted)",
    fontStyle: "italic",
    lineHeight: 1.4,
    margin: "0 0 4px",
  },
  finishList: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 },
  finishRow: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: 10,
    display: "grid",
    gap: 4,
  },
  finishHeader: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
  },
  finishTypeChip: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--gold)",
    background: "rgba(249,175,54,0.12)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 999,
    padding: "2px 8px",
  },
  finishLabel: { fontSize: 13, color: "var(--text)" },
  finishOptional: { fontSize: 10, fontWeight: 700, color: "var(--muted)", fontStyle: "italic" },
  finishBatch: {
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted)",
    background: "rgba(176,160,140,0.12)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "2px 8px",
  },
  finishInstruction: { margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.4 },
  finishQty: {
    margin: 0,
    fontSize: 12,
    color: "var(--text)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  serviceCtlBlock: {
    margin: "12px 0",
    padding: 10,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
  },
  serviceCtlHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  eyebrow: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  serviceCtlBusy: { fontSize: 11, color: "var(--muted)", fontStyle: "italic" },
  serviceCtlError: {
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 8,
    padding: 8,
    fontSize: 12,
    marginBottom: 8,
  },
  serviceCtlRow: { display: "flex", flexWrap: "wrap", gap: 6 },
  adminActionRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    paddingTop: 10,
    borderTop: "1px solid var(--border)",
    flexWrap: "wrap",
  },
  adminAction: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  phaseHint: { fontSize: 11, color: "var(--muted)", fontStyle: "italic" },
  aliasesLink: {
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 700,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    minHeight: 36,
  },
  versionPanel: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 12,
    minWidth: 0,
  },
  versionRow: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
  },
  versionRowTop: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  versionNumber: {
    fontSize: 14,
    fontWeight: 800,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  versionMeta: { fontSize: 11, color: "var(--muted)", fontWeight: 600 },
  versionChangeSummary: {
    margin: "6px 0 0",
    fontSize: 12,
    color: "var(--text)",
    lineHeight: 1.35,
    fontStyle: "italic",
  },
  versionActions: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 },
  versionError: {
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    padding: 8,
    borderRadius: 8,
    fontSize: 11,
    marginBottom: 8,
  },
  editVersionBtn: {
    background: "transparent",
    color: "var(--gold)",
    border: "1px solid rgba(249,175,54,0.4)",
    borderRadius: 8,
    padding: "8px 14px",
    minHeight: 36,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  approveBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "8px 14px",
    minHeight: 36,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  retireBtn: {
    background: "transparent",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    padding: "8px 14px",
    minHeight: 36,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  restoreBtn: {
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "8px 14px",
    minHeight: 36,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  subtleNote: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    fontStyle: "italic",
    paddingTop: 4,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 7px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  muted: { color: "var(--muted)", fontSize: 13, padding: "8px 0" },
};
